import fs from 'fs';
import path from 'path';
import {getStrapiRedirects} from '../../config/strapi-redirects';
import type {GetStaticPaths, GetStaticProps} from 'next';
import {getFooterLinksPerLang} from '@app/modules/ssg-data/unlogged-data';
import {serverSideTranslations} from '@app/modules/locale-management/server';
import {LandingSections} from '@tempo/landing';
import {TempoLandingPage} from '@app/components/layout/LandingPage';
import type {LandingProps} from '@app/types/next';
import {fetchEntryBySlug, getEntriesSlugs} from '@app/modules/strapi/api';
import {StrapiWrapper} from '@app/modules/strapi/components/StrapiWrapper';
import {getTranslationNamespaceForStrapiPage} from '@app/modules/strapi/helpers';

const FeaturePage: React.FC<LandingProps & {featureData: any | null}> = ({
	featureData,
}) => (
	<TempoLandingPage title="" description="">
		<LandingSections topGutter>
			<StrapiWrapper strapiData={featureData} contentType="feature" />
		</LandingSections>
	</TempoLandingPage>
);

export const getStaticProps: GetStaticProps<LandingProps> = async ({
	locale = '',
	locales = [],
	params,
}) => {
	const {feature} = params as {feature: string[]};

	// Get the feature data from strapi-data folder if it exists, otherwise fetch it from Strapi like in the getStaticPaths function.
	const featureDataPath = path.join(
		process.cwd(),
		'strapi-data',
		'feature',
		`${feature[0]}.json`,
	);
	const featureData = fs.existsSync(featureDataPath)
		? JSON.parse(fs.readFileSync(featureDataPath, 'utf-8'))
		: await fetchEntryBySlug('feature', feature[0]);

	const translations = await serverSideTranslations(locale, [
		'common',
		getTranslationNamespaceForStrapiPage('feature', feature[0]) as any,
	]);
	return {
		props: {
			...translations,
			dehydratedState: {
				...(await getFooterLinksPerLang({locale, locales})),
			},
			tracking: {
				pagecategory: 'features',
				pagename: `feature-${feature[0]}`,
				cdpViewId: `features_${feature[0]}` as any,
			},
			featureData: featureData || null,
		},
	};
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const staticSlugPathes = [
		'flow',
		'lyrics',
		'music-quiz',
		'offline',
		'purpleclub',
		'shaker',
		'waze',
	];
	const paths: {params: {feature: string[]}; locale: string}[] = [];

	// If the strapi-data directory exists, read the slugs from the JSON files; otherwise, fetch them from Strapi
	const strapiDataDir = path.join(process.cwd(), 'strapi-data', 'feature');
	const featureSlugs = fs.existsSync(strapiDataDir)
		? fs
				.readdirSync(strapiDataDir)
				.filter((file) => file.endsWith('.json'))
				.map((file) => path.basename(file, '.json'))
		: await getEntriesSlugs('feature');

	// Redirections are handled in next.config.js (redirect is not allowed in getStaticProps while prerendering)
	const redirectionSlugs: string[] = getStrapiRedirects()
		.filter((r: {source: string}) => r.source.startsWith('/features/'))
		.map((r: {source: string}) => r.source.slice('/features/'.length));

	ctx.locales?.forEach((locale) => {
		featureSlugs
			.filter(
				(slug: string) =>
					!staticSlugPathes.includes(slug) && !redirectionSlugs.includes(slug),
			)
			.forEach((slug: string) => {
				paths.push({params: {feature: [slug]}, locale});
			});
	});

	return {paths, fallback: false};
};

export default FeaturePage;
